/**
 * "Choose what to run" — a run limited to some objects, surfaces or personas.
 * ("Run all tests" in the header starts a run of every case without asking.)
 */

import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Play } from 'lucide-react'
import Button from '@/components/common/Button'
import Modal from '@/components/common/Modal'
import { apiError, listCases, startRun, type CaseRow, type StartRunBody } from '@/api/uiTesting'
import { inputStyle, mutedText } from './shared'
import { nice } from './plainSteps'

export default function RunModal({ project, personas, onClose, onStarted }: {
  project: string; personas: string[]; onClose: () => void; onStarted: (runId: string) => void
}) {
  const [cases, setCases] = useState<CaseRow[] | null>(null)
  const [objects, setObjects] = useState<string[]>([])
  const [surfaces, setSurfaces] = useState<string[]>([])
  const [chosenPersonas, setChosenPersonas] = useState<string[]>([])
  const [trace, setTrace] = useState<'failed' | 'all' | 'none'>('failed')
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    let alive = true
    listCases(project)
      .then((r) => { if (alive) setCases(r.cases) })
      .catch((err) => {
        toast.error(apiError(err, 'Could not load the cases'))
        if (alive) setCases([])
      })
    return () => { alive = false }
  }, [project])

  const allObjects = Array.from(new Set((cases || []).map((c) => c.object).filter(Boolean) as string[])).sort()
  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v])
  const selected = (cases || []).filter((c) =>
    (!objects.length || objects.includes(c.object || ''))
    && (!surfaces.length || surfaces.includes(c.surface || 'app'))
    && (!chosenPersonas.length || chosenPersonas.includes(c.persona || 'owner')))
  const invalid = selected.filter((c) => c.errors > 0).length

  const onStart = async () => {
    setStarting(true)
    try {
      const body: StartRunBody = { trace }
      if (objects.length) body.objects = objects
      if (surfaces.length) body.surfaces = surfaces
      if (chosenPersonas.length) body.personas = chosenPersonas
      const r = await startRun(project, body)
      toast.success(`Run ${r.run_id} started — ${r.cases} case(s)`
        + (r.skipped_invalid ? `, ${r.skipped_invalid} skipped as invalid` : ''))
      onStarted(r.run_id)
    } catch (err) {
      toast.error(apiError(err, 'Could not start the run'))
    } finally {
      setStarting(false)
    }
  }

  const pill = (active: boolean) => ({
    padding: '3px 10px', borderRadius: 12, fontSize: 13, cursor: 'pointer',
    border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
    background: active ? 'var(--color-accent)' : 'transparent',
    color: active ? '#fff' : 'var(--color-text)',
  })

  return (
    <Modal open onClose={onClose} title="Run tests" size="wide" footer={
      <>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button icon={<Play size={15} />} loading={starting} onClick={onStart}
                disabled={!cases || selected.length === 0}>
          Run {selected.length} case{selected.length === 1 ? '' : 's'}
        </Button>
      </>
    }>
      {!cases ? <div style={mutedText}>Loading cases…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14 }}>
          <div style={mutedText}>Leave a group empty to include everything. Cases run in a fresh headless
            browser each, against this application as their persona.</div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Objects</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', maxHeight: 140, overflowY: 'auto' }}>
              {allObjects.map((o) => (
                <button key={o} type="button" style={pill(objects.includes(o))}
                        onClick={() => toggle(objects, setObjects, o)}>{nice(o, true)}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Surfaces</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['app', 'site'].map((s) => (
                <button key={s} type="button" style={pill(surfaces.includes(s))}
                        onClick={() => toggle(surfaces, setSurfaces, s)}>
                  {s === 'app' ? 'Application' : 'Website'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Personas</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {personas.map((p) => (
                <button key={p} type="button" style={pill(chosenPersonas.includes(p))}
                        onClick={() => toggle(chosenPersonas, setChosenPersonas, p)}>{p}</button>
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>Playwright trace</span>
            <select value={trace} onChange={(e) => setTrace(e.target.value as typeof trace)} style={inputStyle}>
              <option value="failed">Keep for failed cases</option>
              <option value="all">Keep for every case</option>
              <option value="none">Do not record</option>
            </select>
          </label>
          {invalid > 0 && (
            <div style={{ color: 'var(--color-warning, #d97706)' }}>
              {invalid} selected case(s) have validation errors and will be skipped.
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
